import { GripVertical } from "lucide-react";
import * as React from "react";
import {
  Panel,
  PanelGroup,
  type PanelGroupProps,
  type PanelProps,
  PanelResizeHandle,
  type PanelResizeHandleProps,
} from "react-resizable-panels";
import { cx } from "../internal/cx.js";

/**
 * Resizable — split-pane layout with draggable dividers.
 *
 * Wraps react-resizable-panels with v2 styling. Use for IDE-style split
 * views: editor + preview, list + detail, dual scroll columns. NOT a
 * substitute for Sheet — Resizable splits a single page, Sheet overlays
 * a panel from an edge.
 *
 * The handle exposes a `withHandle` boolean to render a visible grip
 * icon at the center of the divider; without it, the divider is a 1-px
 * hover-thickened line.
 */

export type ResizableGroupProps = PanelGroupProps & {
  className?: string;
  /**
   * Stack panels and hide resize handles below phone width. Defaults to true
   * because drag-resizing split panes is a desktop interaction.
   */
  mobileStack?: boolean;
};

// Explicit return type — without it the dts step can't infer a portable
// type for ResizableGroup because react-resizable-panels' declaration
// sits inside its package's `dist/declarations/...` path, which isn't
// exposed via its public types.
export const ResizableGroup: React.ForwardRefExoticComponent<
  ResizableGroupProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, ResizableGroupProps>(function ResizableGroup(
  { className, direction = "horizontal", mobileStack = true, ...props },
  _ref,
) {
  return (
    <PanelGroup
      direction={direction}
      className={cx(
        "cw-v2-resizable-group",
        `cw-v2-resizable-group--${direction}`,
        mobileStack && "cw-v2-resizable-group--mobile-stack",
        className,
      )}
      {...props}
    />
  );
});
ResizableGroup.displayName = "ResizableGroup";

export type ResizablePanelProps = PanelProps & { className?: string };

// Explicit return type for the same dts-portability reason as ResizableGroup.
export const ResizablePanel: React.ForwardRefExoticComponent<
  ResizablePanelProps & React.RefAttributes<HTMLElement>
> = React.forwardRef<HTMLElement, ResizablePanelProps>(function ResizablePanel(
  { className, ...props },
  _ref,
) {
  return <Panel className={cx("cw-v2-resizable-panel", className)} {...props} />;
});
ResizablePanel.displayName = "ResizablePanel";

export interface ResizableHandleProps extends PanelResizeHandleProps {
  /** Render the visible grip icon at the center of the divider. */
  withHandle?: boolean;
  className?: string;
}

// Explicit return type for the same dts-portability reason as ResizableGroup.
export const ResizableHandle: React.ForwardRefExoticComponent<
  ResizableHandleProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, ResizableHandleProps>(function ResizableHandle(
  { className, withHandle, ...props },
  _ref,
) {
  // The underlying handle gets `role="separator"` + `tabindex="0"` from
  // react-resizable-panels but the library doesn't emit
  // `aria-valuenow/min/max`, which axe-core's `aria-required-attr` rule
  // considers mandatory for focusable separators. Provide sensible
  // defaults (a 0–100 scale starting at 50) so the handle satisfies the
  // ARIA contract; consumers can still override via spread.
  return (
    <PanelResizeHandle
      className={cx("cw-v2-resizable-handle", className)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={50}
      {...props}
    >
      {withHandle ? (
        <span className="cw-v2-resizable-handle__grip" aria-hidden>
          <GripVertical className="cw-v2-resizable-handle__grip-icon" />
        </span>
      ) : null}
    </PanelResizeHandle>
  );
});
ResizableHandle.displayName = "ResizableHandle";
